"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Layers } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface SolutionItem {
  title: string;
  category: string;
  description: string;
  path: string;
  image: string;
  imageSrcset: string;
  imageSizes: string;
  iconName: "cpu" | "layers";
  highlights: string[];
}
const solutionsList: SolutionItem[] = [{
  title: "Process Analytical Technology",
  category: "Real-Time Monitoring",
  description: "In-line and on-line process monitoring systems designed to track critical quality attributes (CQAs) and optimize batch consistency in regulated manufacturing.",
  path: "/ProcessAnalyticalTechnologyPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_solution.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_solution.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_analytical_technology_solution.png-wvc-sizes",
  iconName: "cpu",
  highlights: ["Continuous quantitative trend analysis", "Seamless integration with SCADA & control loops", "Compliant with 21 CFR Part 11 requirements"]
}, {
  title: "Blend Uniformity",
  category: "Solid Dosage Quality",
  description: "High-precision spectroscopic testing and powder analysis workflows to evaluate mixing kinetics and ensure active pharmaceutical ingredient (API) homogeneity.",
  path: "/BlendUniformityPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_solution.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_solution.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_solution.png-wvc-sizes",
  iconName: "layers",
  highlights: ["Non-destructive real-time powder analysis", "Eliminates thief sampling errors and segregation", "Rapid statistical endpoint determination"]
}];
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedsolutions" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl space-y-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              Complementary Applications
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Explore Related Solutions
            </h2>
            <p className="text-base md:text-lg text-muted-foreground">
              Accelerate your laboratory analysis and process control with
              specialized instrumentation, application expertise, and lifecycle
              support.
            </p>
          </div>

          <div>
            <Button variant="outline" asChild className="gap-2">
              <Link to="/SolutionsPage">
                <span>View All Solutions</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutionsList.map((item, index) => {
          const Icon = item.iconName === "cpu" ? Cpu : Layers;
          return <motion.div key={item.title} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 24
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.15
          }} className="h-full">
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    {/* Media Container */}
                    <div className="relative aspect-16/9 w-full overflow-hidden bg-muted">
                      <img src={item.image} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 left-4">
                        <Badge variant="secondary" className="bg-background/90 backdrop-blur-xs text-foreground shadow-xs border border-border">
                          {item.category}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="space-y-2 pt-6">
                      <div className="flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                          <Icon className="size-5" />
                        </div>
                        <CardTitle className="text-xl md:text-2xl text-foreground font-semibold">
                          {item.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-sm md:text-base text-muted-foreground pt-2 leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <div className="rounded-lg bg-secondary/50 p-4 border border-border/50">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                          Key Capabilities
                        </p>
                        <ul className="space-y-2.5">
                          {item.highlights.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-sm text-foreground">
                              <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                              <span>{point}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 pb-6">
                    <Button asChild className="w-full justify-between group">
                      <Link to={item.path}>
                        <span>Explore {item.title}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}